import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "./Employee.css";
import EmployeeForm from "./EmployeeForm";
import { useCombinedContext } from "../../context/CombinedContext";
import exportToExcel from "../Excel/ExportToExcel";

const EmployeeList = () => {
  const [employee, setEmployee] = useState([]);
  const [vessel, setVessel] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Active | Inactive
  const [fromDate, setFromDate] = useState(""); // Start date for filtering
  const [toDate, setToDate] = useState(""); // End date for filtering
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(""); // For year-wise filtering
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // New state: sorting by a field (either "expiryDate" or "dateOfJoin")
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const { selectesVessel, selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;

  // console.log("for gst",selectedCompany)
  // console.log("company Code", companyCode);
  //  console.log("companyId: ", companyId);
  // console.log("vessel", vessel)

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;

      const response = await axiosInstance.get(
        `/employee/report/${companyId}`,
        {
          params,
        },
      );
      setEmployee(response.data.data || []);
      // console.log(response);
    } catch (error) {
      // console.error("Error fetching employee:", error);
      toast.error("Error fetching employee");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchEmployee();
    }
  }, [companyId, fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(0);
  };

  // Search + Active/Inactive filtering, derived from the fetched employees
  // so both controls always stay in sync with each other and with the data.
  const filteredEmployees = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return employee.filter((emp) => {
      const matchesSearch =
        !text ||
        emp.name?.toString().toLowerCase().includes(text) ||
        emp.employeeNo?.toString().toLowerCase().includes(text);

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" ? !!emp.status : !emp.status);

      return matchesSearch && matchesStatus;
    });
  }, [employee, searchText, statusFilter]);

  const deleteCompany = async (_id) => {
    try {
      await axiosInstance.delete(`/employee/${_id}`);
      fetchEmployee();
    } catch (error) {
      // console.error("Error deleting company:", error);
      toast.error("Failed to delete conpany");
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setIsDeleteModalOpen(true);
  };

  // Toggle employee Active/Inactive status
  const handleToggleStatus = async (emp) => {
    try {
      const updatedStatus = !emp.status;

      await axiosInstance.patch(`/employee/${emp._id}/status`, {
        status: updatedStatus,
      });

      const updatedEmployees = employee.map((e) =>
        e._id === emp._id ? { ...e, status: updatedStatus } : e,
      );
      setEmployee(updatedEmployees);

      toast.success(
        updatedStatus
          ? "Employee activated successfully."
          : "Employee deactivated successfully.",
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update employee status.",
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/employee/${deleteTarget}`);
      setEmployee((prev) => prev.filter((loc) => loc._id !== deleteTarget));
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      toast.success("Location deleted successfully");
    } catch (error) {
      // console.error("Error deleting location:", error);
      toast.error("Failed to delete location");
    }
  };

  // Get data from selected year
  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);

    if (year) {
      setFromDate(`${year}-01-01`);
      setToDate(`${year}-12-31`);
    } else {
      setFromDate("");
      setToDate("");
    }
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };
  // const itemsPerPage = 5;

  // Toggle sort for a given field.
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  // Sort data based on the selected sort field and order.
  const sortedData = [...filteredEmployees].sort((a, b) => {
    if (!sortField) return 0;
    let dateA, dateB;
    if (sortField === "expiryDate") {
      dateA = a.expiryDate ? new Date(a.expiryDate) : new Date(0);
      dateB = b.expiryDate ? new Date(b.expiryDate) : new Date(0);
    } else if (sortField === "dateOfJoin") {
      dateA = a.dateOfJoin ? new Date(a.dateOfJoin) : new Date(0);
      dateB = b.dateOfJoin ? new Date(b.dateOfJoin) : new Date(0);
    }
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });
  const currentEmployeeItems = sortedData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );
  const pageCount = Math.ceil(filteredEmployees.length / itemsPerPage);

  //Check Role
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));

  const userRole = authData.role;

  const handleExport = () => {
    // Export every record matching the current search + status filter
    // (all pages), not just the rows on the visible page.
    const tableData = [
      [
        "Sl No",
        "Company code",
        "Employee Name",
        "Employee No",
        "Date of Join",
        "Expire Date",
        "Bank Name",
        "Account No",
      ],
      ...sortedData.map((employee, index) => [
        index + 1, // Sl No
        companyCode || "N/A", // SSG Yard
        employee.name || "N/A", // SSG Yard
        employee.employeeNo || "N/A", // Company Name
        employee.dateOfJoin
          ? new Date(employee.dateOfJoin).toLocaleDateString() // Employee Date
          : "N/A",
        employee.expiryDate
          ? new Date(employee.expiryDate).toLocaleDateString() // Employee Date
          : "N/A",
        employee.bankName || "N/A", // Employee No
        employee.AccountNo || "N/A", // Employee No
      ]),
    ];
    exportToExcel(tableData, "Employee-data.xlsx");
  };

  return (
    <div>
      <ToastContainer />
      <div className="mb-4 headStyle d-flex  justify-content-between align-items-center">
        <h4>Employee</h4>
        <button
          className="button-2"
          onClick={() => {
            if (selectedCompany?.status === false) {
              toast.error(
                "The selected company is inactive. Cannot add a employee.",
              );
            } else {
              setSelectedData(null);
              setShowForm(true);
            }
          }}
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
        >
          Add Employee
        </button>
      </div>
      <div className="headStyle">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <label
              htmlFor="fromDate"
              style={{ width: "-webkit-fill-available" }}
            >
              From Date
            </label>
            <input
              className="form-control"
              type="date"
              placeholder="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{
                border: "1px solid #1E2A5E",
                borderRadius: "8px",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <label
              htmlFor="fromDate"
              style={{ width: "-webkit-fill-available" }}
            >
              To Date
            </label>
            <input
              className="form-control"
              type="date"
              placeholder="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{
                border: "1px solid #1E2A5E",
                borderRadius: "8px",
              }}
            />
          </div>
          <div style={{ flex: "0 1 auto" }}>
            <select
              className="form-select"
              value={selectedYear}
              onChange={handleYearChange}
              // size={30}
              style={{
                width: "70px", // Adjust the width
                maxHeight: "300px !importent", // Limit the height of the dropdown
                overflowY: "auto", // Add scroll for long lists
              }}
            >
              <option value="">Year</option>
              {Array.from(
                { length: new Date().getFullYear() - 2000 + 1 },
                (_, i) => 2000 + i,
              ).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="headStyle mt-4 ">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search..."
                style={{
                  maxWidth: "100%",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                onChange={handleSearchChange}
              />
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1" htmlFor="statusFilter">
                Status Filter
              </label>
              <select
                id="statusFilter"
                className="form-select no-arrow"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          {["admin", "superadmin"].includes(userRole) && (
            <div style={{ flex: "0 1 auto" }}>
              <button className="button-2 exxportBtn" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          )}
        </div>
        <div
          className="my-3 rounded table-responsive-sm"
          style={{
            overflowX: "auto",
          }}
        >
          <table id="my-table" className="table table-bordered">
            <thead className="t_head">
              <tr className="text-center align-middle">
                {/* Table Headers */}
                <th className="align-middle">Sl No</th>
                <th className="align-middle">Employee Name</th>
                <th className="align-middle">Employee No</th>
                <th className="align-middle">Status</th>
                <th className="align-middle">Address</th>
                <th
                  className="align-middle"
                  onClick={() => toggleSort("dateOfJoin")}
                  style={{ cursor: "pointer" }}
                >
                  Date of join{" "}
                  {sortField === "dateOfJoin" &&
                    (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="align-middle"
                  onClick={() => toggleSort("expiryDate")}
                  style={{ cursor: "pointer" }}
                >
                  Expiry date{" "}
                  {sortField === "expiryDate" &&
                    (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th className="align-middle">Bank Name</th>
                <th className="align-middle">Account Number</th>
                <th className="align-middle">Loan Amount</th>
                <th className="align-middle">Food Allowance</th>
                <th className="align-middle">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {currentEmployeeItems.length === 0 ? (
                    <>
                      <tr>
                        <td colSpan="15">
                          <div className="d-flex justify-content-center m-1">
                            <h6>Data Not Found</h6>
                          </div>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <>
                      {currentEmployeeItems &&
                        currentEmployeeItems.map((employee, index) => (
                          <tr key={employee._id || index}>
                            {/* Table Data */}
                            <td>{currentPage * itemsPerPage + index + 1}</td>
                            <td>{employee.name || "N/A"}</td>
                            <td>{employee.employeeNo || "N/A"}</td>
                            <td>
                              {["admin", "superadmin", "user"].includes(userRole) ? (
                                <div className="form-check form-switch employee-status-switch d-flex justify-content-center align-items-center gap-2 m-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={!!employee.status}
                                    onChange={() => handleToggleStatus(employee)}
                                    style={{ cursor: "pointer" }}
                                    title={
                                      employee.status
                                        ? "Click to deactivate"
                                        : "Click to activate"
                                    }
                                  />
                                  <span
                                    className={`badge ${
                                      employee.status ? "bg-success" : "bg-danger"
                                    }`}
                                  >
                                    {employee.status ? "Active" : "Inactive"}
                                  </span>
                                </div>
                              ) : (
                                <span
                                  className={`badge ${
                                    employee.status ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {employee.status ? "Active" : "Inactive"}
                                </span>
                              )}
                            </td>
                            <td>{employee.address || "N/A"}</td>

                            <td>
                              {employee.dateOfJoin
                                ? new Date(
                                    employee.dateOfJoin,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td>
                              {employee.expiryDate
                                ? new Date(
                                    employee.expiryDate,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td>{employee.bankName || "N/A"}</td>
                            <td>{employee.AccountNo || "N/A"}</td>
                            <td>{employee.loanAmount || 0}</td>
                            <td>{employee.foodAllowance || 0}</td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => {
                                    setSelectedData(employee);
                                    setShowForm(true);
                                  }}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                {["admin", "superadmin"].includes(userRole) && (
                                  <>
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDelete(employee._id)}
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />

        {showForm && (
          <EmployeeForm
            employee={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            vessel={vessel}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowForm(false);
              fetchEmployee();
            }}
          />
        )}
        {isDeleteModalOpen && (
          <div
            className={`modal fade ${isDeleteModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="deleteModalLabel"
            aria-hidden={!isDeleteModalOpen}
            onClick={(e) => {
              if (e.target.classList.contains("modal"))
                setIsDeleteModalOpen(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="deleteModalLabel">
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setIsDeleteModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body modalWhite">
                  Are you sure you want to delete this location? This action
                  cannot be undone.
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeList;
